import './FilterOptionMultiLevelCollections.scss';

import ArrowButton from '@components/ArrowButton';
import type { FilterOptionProps } from '@components/FilterOption';
import FilterOption from '@components/FilterOption';
import type { FilterOptionItemSettings } from '@components/FilterOptionItem';
import { FilterOptionItem } from '@components/FilterOptionItem';
import FilterOptionItemButton from '@components/FilterOptionItem/Button';
import FilterOptionItemLabel from '@components/FilterOptionItem/Label';
import type { FilterOptionType } from '@providers/FilterProvider';
import type { MaybeArrayItem } from '@types';
import { createClsNameMap, mapModifiers, stripHtml, toItemArray } from '@utils';

import FilterOptionItemSecondLevelCollections from './FilterOptionSecondLevelCollections';

export const clsNameMap = createClsNameMap({
  elements: {
    firstlevel: createClsNameMap(),
    listitem: createClsNameMap(),
  },
  modifiers: ['disabled'],
})('filter-option-item-multilevel-collections');

export type FilterOptionCollectionItemHookProps = {
  collectionId: string;
  collectionHandle: string;
  hasChildListFilterOptionItem?: boolean;
  tag?: string;
  subTags?: Array<{
    tag: string;
  }>;
};

export type FilterOptionCollectionItemProps = {
  isOptionItemSelected: boolean;
  collectionUrlPath: string;
  isChildListExpanded: boolean | undefined;
  toggleChildListExpandedState: () => void;
  handleSelectOptionItem: (e: Event) => void;
  showFilterOptionCount: boolean;
};

export type FilterOptionMultiLevelCollectionsItemProps = {
  value: {
    handle: string;
    displayName: string;
    collectionId: string;
    docCount?: number;
    tags: Array<{
      tag: string;
      displayName: string;
      subTags?: Array<{
        tag: string;
        displayName: string;
      }>;
    }>;
    settings: FilterOptionItemSettings;
  };
  useFilterOptionCollectionItem: (
    item: FilterOptionCollectionItemHookProps
  ) => FilterOptionCollectionItemProps;
  keepValuesStatic: boolean;
};

export type FilterOptionMultiLevelCollectionsProps<FilterOption extends FilterOptionType> = {
  option: FilterOption;
  filterOptionItemProps: (
    optionItem: MaybeArrayItem<FilterOption['values']>
  ) => FilterOptionMultiLevelCollectionsItemProps['value'];
  useFilterOptionCollectionItem: (
    item: FilterOptionCollectionItemHookProps
  ) => FilterOptionCollectionItemProps;
  filterOptionProps?: Partial<FilterOptionProps>;
};

const FilterOptionMultiLevelCollectionsItem = ({
  value: { handle, displayName, collectionId, tags, settings, docCount },
  useFilterOptionCollectionItem,
  keepValuesStatic,
}: FilterOptionMultiLevelCollectionsItemProps) => {
  const {
    isOptionItemSelected,
    collectionUrlPath,
    isChildListExpanded,
    toggleChildListExpandedState,
    handleSelectOptionItem,
    showFilterOptionCount,
  } = useFilterOptionCollectionItem({
    collectionId,
    collectionHandle: handle,
    hasChildListFilterOptionItem: !!tags?.length,
  });
  // Hide docCount of 'All'
  const showCollectionItemCount = handle === 'all' ? false : showFilterOptionCount;
  const disabledItem = !keepValuesStatic && docCount === 0 && handle !== 'all';

  if (!stripHtml(displayName)) return null;

  return (
    <FilterOptionItem
      className={mapModifiers(clsNameMap, {
        disabled: disabledItem,
      })}
    >
      <div className={clsNameMap.elm('firstlevel')}>
        <FilterOptionItemButton
          as='link'
          isSelected={isOptionItemSelected}
          label={<FilterOptionItemLabel settings={settings} label={displayName} />}
          docCount={docCount}
          showFilterOptionCount={showCollectionItemCount}
          link={collectionUrlPath}
          onClick={(e: Event) => handleSelectOptionItem(e)}
        />
        {!!tags?.length && (
          <ArrowButton
            label={`Expand/Collapse ${displayName}`}
            modifiers={isChildListExpanded ? ['bottom'] : ['right']}
            onClick={toggleChildListExpandedState}
            isFilter={true}
          />
        )}
      </div>
      {!!tags?.length && isChildListExpanded && (
        <ul className={clsNameMap.elm('listitem')}>
          <FilterOptionItemSecondLevelCollections
            collectionId={collectionId}
            collectionHandle={handle}
            useFilterOptionCollectionItem={useFilterOptionCollectionItem}
            options={tags.map((item) => ({
              ...item,
              settings,
            }))}
          />
        </ul>
      )}
    </FilterOptionItem>
  );
};

const FilterOptionMultiLevelCollections = <FilterOption extends FilterOptionType>({
  option,
  filterOptionItemProps,
  useFilterOptionCollectionItem,
  filterOptionProps,
}: FilterOptionMultiLevelCollectionsProps<FilterOption>) => {
  return (
    <FilterOption option={option} {...filterOptionProps}>
      {({ filterOptions }) =>
        toItemArray(filterOptions).map((optionItem) => {
          const optionItemValue = filterOptionItemProps(optionItem);
          optionItemValue.docCount = optionItem.doc_count;

          return (
            <FilterOptionMultiLevelCollectionsItem
              useFilterOptionCollectionItem={useFilterOptionCollectionItem}
              key={optionItemValue.handle}
              keepValuesStatic={option.keepValuesStatic}
              value={optionItemValue}
              {...filterOptionProps}
            />
          );
        })
      }
    </FilterOption>
  );
};

export default FilterOptionMultiLevelCollections;
